import { Colors, FOCUS_RING, Icon, Pagination, Text } from '@amelio/ui-library';

import { COMPENSATION_TABLE_GRID, COMPENSATION_TABLE_HEADER } from '../compensationStyles';
import { COMPENSATION_TABLE_PAGE_SIZE } from '../constants';
import { AdjustmentRow } from './AdjustmentRow';

import type { AdjustmentsSort } from '../hooks/useAdjustmentsTable';
import type { CompensationLineSortKey } from '../utils/sortLines';
import type { TranslationKey } from '@/i18n-key';
import type {
    CompensationDraftLine,
    CompensationLevel,
    CompensationLine,
} from '@/types/compensation';
import type { IconProps } from '@amelio/ui-library';

import { useAmelioTranslations } from '@/hooks';

/** The seven columns, in order. The note is free text and would sort on nothing meaningful. */
const COLUMNS: {
    labelKey: TranslationKey;
    sortKey: CompensationLineSortKey | null;
    align: 'left' | 'center' | 'right';
}[] = [
    { labelKey: 'compensation.adjustments.columns.person', sortKey: 'name', align: 'left' },
    { labelKey: 'compensation.adjustments.columns.performance', sortKey: 'score', align: 'left' },
    { labelKey: 'compensation.adjustments.columns.salary', sortKey: 'salary', align: 'right' },
    {
        labelKey: 'compensation.adjustments.columns.proposed',
        sortKey: 'proposedPercent',
        align: 'center',
    },
    {
        labelKey: 'compensation.adjustments.columns.increase',
        sortKey: 'appliedPercent',
        align: 'center',
    },
    {
        labelKey: 'compensation.adjustments.columns.newSalary',
        sortKey: 'newSalary',
        align: 'right',
    },
    { labelKey: 'compensation.adjustments.columns.note', sortKey: null, align: 'left' },
];

/** The caret of the one column being sorted on, in the direction it is sorted in. */
const SORT_ICON_NAME: Record<AdjustmentsSort['direction'], IconProps['name']> = {
    asc: 'arrow-arrow-up-1',
    desc: 'arrow-arrow-down-1',
};

/** The caret of every other sortable column. A neutral two-way glyph, because an arrow pointing up
 * is a claim: with nothing sorted, six ascending arrows told the reader the table was sorted six
 * ways at once. It occupies the same width, so the affordance the `COLUMNS` comment reserves room
 * for is still there. */
const UNSORTED_ICON_NAME: IconProps['name'] = 'essential-sort';

const ALIGNMENT_CLASS = {
    left: 'justify-start text-left',
    center: 'justify-center text-center',
    right: 'justify-end text-right',
} as const;

/**
 * What one row reports back: who the change is about, and the value it changes. Objects rather
 * than a pair of positional arguments because a note change carries two strings — positionally,
 * a swapped call site would compile, and the note would be written under a person's own id.
 */
export interface LineSalaryChange {
    employeeId: string;
    /** Whole dollars, or `null` when the field was emptied. */
    salary: number | null;
}

export interface LinePercentChange {
    employeeId: string;
    /** The hand-entered increase, or `null` to hand the row back to the distribution. */
    percent: number | null;
}

export interface LineLockChange {
    employeeId: string;
    /** The amount frozen on the row, or `null` to hand the row back to the distribution. */
    lockedAmount: number | null;
}

export interface LineNoteChange {
    employeeId: string;
    note: string;
}

interface ColumnSortStateOptions {
    /** `null` for the note column, which sorts on nothing and therefore announces nothing. */
    sortKey: CompensationLineSortKey | null;
    sort: AdjustmentsSort | null;
}

/** What a column header announces to a screen reader: the direction it is sorted in, `none` for a
 * sortable column nobody is sorting on, and nothing at all for a column that cannot be sorted. */
const resolveAriaSort = ({
    sortKey,
    sort,
}: ColumnSortStateOptions): 'ascending' | 'descending' | 'none' | undefined => {
    if (sortKey === null) {
        return undefined;
    }

    if (sort === null || sort.key !== sortKey) {
        return 'none';
    }

    return sort.direction === 'asc' ? 'ascending' : 'descending';
};

interface AdjustmentsTableProps {
    /** The page's worth of computed lines, already searched, sorted and sliced. */
    pageLines: CompensationLine[];
    draftLinesById: Map<string, CompensationDraftLine>;
    levelsById: Map<string, CompensationLevel>;
    sort: AdjustmentsSort | null;
    onToggleSort: (key: CompensationLineSortKey) => void;
    page: number;
    totalPages: number;
    matchCount: number;
    onPageChange: (page: number) => void;
    canEdit: boolean;
    canRecordSalaries: boolean;
    onSalaryChange: (change: LineSalaryChange) => void;
    onPercentChange: (change: LinePercentChange) => void;
    onToggleLock: (line: CompensationLine) => void;
    onNoteChange: (change: LineNoteChange) => void;
}

/**
 * The people of the envelope, fifteen at a time. A CSS grid rather than a `<table>` — the same
 * choice the competency matrix made — carrying the table ARIA roles, so the sortable headers can
 * announce their direction with `aria-sort`.
 */
export const AdjustmentsTable = ({
    pageLines,
    draftLinesById,
    levelsById,
    sort,
    onToggleSort,
    page,
    totalPages,
    matchCount,
    onPageChange,
    canEdit,
    canRecordSalaries,
    onSalaryChange,
    onPercentChange,
    onToggleLock,
    onNoteChange,
}: AdjustmentsTableProps) => {
    const { t } = useAmelioTranslations();

    return (
        <div className={'flex flex-col gap-[16px]'}>
            <div className={'scrollbar-glass overflow-x-auto'}>
                <div
                    role={'table'}
                    aria-label={t('compensation.adjustments.title')}
                    className={'min-w-[1077px]'}
                    data-testid={'compensation-adjustments-table'}
                >
                    <div role={'rowgroup'}>
                        <div
                            role={'row'}
                            className={`${COMPENSATION_TABLE_GRID} border-base-100 border-b`}
                        >
                            {COLUMNS.map(({ labelKey, sortKey, align }) => {
                                const isSorted = sort !== null && sort.key === sortKey;

                                return (
                                    <div
                                        key={labelKey}
                                        role={'columnheader'}
                                        aria-sort={resolveAriaSort({ sortKey, sort })}
                                        className={'py-[10px]'}
                                    >
                                        {sortKey === null ? (
                                            <Text
                                                color={Colors.BASE_500}
                                                className={`${COMPENSATION_TABLE_HEADER} ${ALIGNMENT_CLASS[align]} block`}
                                            >
                                                {t(labelKey)}
                                            </Text>
                                        ) : (
                                            <button
                                                type={'button'}
                                                onClick={() => onToggleSort(sortKey)}
                                                data-testid={`compensation-sort-${sortKey}`}
                                                className={`${FOCUS_RING} ${COMPENSATION_TABLE_HEADER} ${ALIGNMENT_CLASS[align]} ${
                                                    isSorted ? 'text-navy' : 'text-base-500'
                                                } inline-flex w-full items-center gap-1.5`}
                                            >
                                                <span>{t(labelKey)}</span>

                                                {/* The caret never gives up its width to the
                                                    label: a sortable column that has lost its
                                                    arrow looks like a column nobody can sort. */}
                                                <Icon
                                                    name={
                                                        isSorted
                                                            ? SORT_ICON_NAME[sort.direction]
                                                            : UNSORTED_ICON_NAME
                                                    }
                                                    size={'xs'}
                                                    color={
                                                        isSorted ? Colors.PRIMARY : Colors.BASE_400
                                                    }
                                                    className={'flex-shrink-0'}
                                                    ariaHidden
                                                />
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div role={'rowgroup'}>
                        {pageLines.map((line) => {
                            const draftLine = draftLinesById.get(line.employeeId);

                            // A computed line always has a draft line: both are seeded from the same
                            // envelope response. A missing one can only mean a preview raced a tab
                            // switch, and skipping the row beats crashing the page.
                            return draftLine ? (
                                <AdjustmentRow
                                    key={line.employeeId}
                                    line={line}
                                    draftLine={draftLine}
                                    level={
                                        line.performanceLevelId === null
                                            ? undefined
                                            : levelsById.get(line.performanceLevelId)
                                    }
                                    canEdit={canEdit}
                                    canRecordSalaries={canRecordSalaries}
                                    onSalaryChange={(salary) =>
                                        onSalaryChange({ employeeId: line.employeeId, salary })
                                    }
                                    onPercentChange={(percent) =>
                                        onPercentChange({ employeeId: line.employeeId, percent })
                                    }
                                    onToggleLock={() => onToggleLock(line)}
                                    onNoteChange={(note) =>
                                        onNoteChange({ employeeId: line.employeeId, note })
                                    }
                                />
                            ) : null;
                        })}
                    </div>
                </div>
            </div>

            {totalPages > 1 ? (
                <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    totalCount={matchCount}
                    pageSize={COMPENSATION_TABLE_PAGE_SIZE}
                    onPageChange={onPageChange}
                    dataTestId={'compensation-adjustments-pagination'}
                />
            ) : null}
        </div>
    );
};
